// TenantFormFields.jsx
import React from "react";
import {
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  Alert,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const ACCOUNT_TYPES = [
  { value: "production", labelKey: "tenantFormFields.accountType.production" },
  { value: "test", labelKey: "tenantFormFields.accountType.test" },
];

const PAYMENT_METHODS = [
  { value: "", labelKey: "tenantFormFields.paymentMethod.none" },
  { value: "bank_transfer", labelKey: "tenantFormFields.paymentMethod.bankTransfer" },
  { value: "card", labelKey: "tenantFormFields.paymentMethod.card" },
];

export default function TenantFormFields({ form, setForm, disabled, isEdit }) {
  const { t } = useTranslation();
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  return (
    <Grid container spacing={1}>
      {isEdit ? (
        <Grid item xs={12}>
          <Box sx={{ mb: 1 }}>
            <Alert severity="info">
              {t(
                "tenantFormFields.publicInfoNote",
                "Poznámka: Adresa, telefón a e-mail sú verejné údaje pre adminov organizácie (môžu sa zobrazovať v aplikácii). Nie sú to fakturačné ani kontaktné údaje pre superadmina."
              )}
            </Alert>
          </Box>
        </Grid>
      ) : null}

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label={t("tenantFormFields.name", "Názov")}
          name="name"
          value={form.name}
          onChange={onChange}
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label={t("tenantFormFields.slug.label", "Slug")}
          name="slug"
          value={form.slug}
          onChange={onChange}
          disabled={disabled}
          helperText={
            isEdit
              ? t(
                  "tenantFormFields.slug.helperEdit",
                  "Zmena slugu môže ovplyvniť prihlásenie a odkazy."
                )
              : t(
                  "tenantFormFields.slug.helperCreate",
                  "Slug sa použije v URL a pre admin účet."
                )
          }
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label={t("tenantFormFields.description", "Popis")}
          name="description"
          value={form.description}
          onChange={onChange}
          disabled={disabled}
          multiline
          minRows={2}
        />
      </Grid>

      {/* Billing / identity */}
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label={t("tenantFormFields.ico", "IČO")}
          name="ico"
          value={form.ico || ""}
          onChange={onChange}
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label={t("tenantFormFields.dic", "DIČ")}
          name="dic"
          value={form.dic || ""}
          onChange={onChange}
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label={t("tenantFormFields.icDph", "IČ DPH")}
          name="ic_dph"
          value={form.ic_dph || ""}
          onChange={onChange}
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          select
          fullWidth
          label={t("tenantFormFields.accountType.label", "Typ účtu")}
          name="account_type"
          value={form.account_type || "production"}
          onChange={onChange}
          disabled={disabled}
        >
          {ACCOUNT_TYPES.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.value === "production" ? t(o.labelKey, "Produkcia") : t(o.labelKey, "Test")}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          select
          fullWidth
          label={t("tenantFormFields.paymentMethod.label", "Spôsob platby")}
          name="payment_method"
          value={form.payment_method ?? ""}
          onChange={onChange}
          disabled={disabled}
        >
          {PAYMENT_METHODS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.value === ""
                ? t(o.labelKey, "Žiadny")
                : o.value === "bank_transfer"
                ? t(o.labelKey, "Bankový prevod")
                : t(o.labelKey, "Karta")}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControlLabel
          control={
            <Switch
              checked={!!form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              disabled={disabled}
            />
          }
          label={t("tenantFormFields.active", "Aktívny")}
        />
      </Grid>

      {/* Public fields (tenant-admin visible) */}
      {isEdit ? (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("tenantFormFields.address", "Adresa")}
              name="address"
              value={form.address || ""}
              onChange={onChange}
              disabled={disabled}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("tenantFormFields.phone", "Telefón")}
              name="phone"
              value={form.phone || ""}
              onChange={onChange}
              disabled={disabled}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("tenantFormFields.email", "E-mail")}
              name="email"
              value={form.email || ""}
              onChange={onChange}
              disabled={disabled}
            />
          </Grid>
        </>
      ) : null}
    </Grid>
  );
}